import { useEffect, useState, type FormEvent } from 'react';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { AdminEmpty, AdminPageHeader } from '@/features/admin/ui';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';

type AgendaEvent = {
  id: string;
  title: string;
  subject: string;
  starts_at: string | null;
  ends_at: string | null;
  instructions: string;
  created_at: string;
};

function formatAgendaAdminRange(startsAt: string | null, endsAt: string | null): string {
  if (!startsAt && !endsAt) return '—';
  const start = startsAt ? new Date(startsAt).toLocaleString('fr-FR') : '—';
  if (!endsAt) return start;
  const end = new Date(endsAt).toLocaleString('fr-FR');
  return `${start} → ${end}`;
}

export function AdminAgendaPage() {
  const { profile, isAdmin, isStaff } = useAuth();
  const toast = useToast();
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const { data, error: err } = await kuunda
      .from('agenda_events')
      .select('id, title, subject, starts_at, ends_at, instructions, created_at')
      .order('starts_at', { ascending: true, nullsFirst: false });
    if (err) toast.error(err.message);
    else setEvents((data ?? []) as AgendaEvent[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!isStaff || !profile) return;

    if (startsAt && endsAt && new Date(endsAt).getTime() < new Date(startsAt).getTime()) {
      toast.error('La date/heure de fin doit être postérieure ou égale au début.');
      return;
    }

    const { error: err } = await kuunda.from('agenda_events').insert({
      title: title.trim(),
      subject: subject.trim(),
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      instructions: instructions.trim(),
      created_by: profile.id,
    });
    if (err) {
      toast.error(err.message);
      return;
    }
    setTitle('');
    setSubject('');
    setStartsAt('');
    setEndsAt('');
    setInstructions('');
    toast.success('Événement créé.', 'Agenda');
    await load();
  }

  async function remove(id: string) {
    if (!isAdmin) return;
    if (!window.confirm('Supprimer cet événement ?')) return;
    const { error: err } = await kuunda.from('agenda_events').delete().eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success('Événement supprimé.', 'Agenda');
      await load();
    }
  }

  return (
    <div className="admin-page">
      <AdminPageHeader title="Agenda" description="Événements et rendez-vous plateforme." />

      {isStaff ? (
        <section className="admin-panel">
          <div className="admin-panel-title">
            <h3>Nouvel événement</h3>
          </div>
          <form onSubmit={onCreate}>
            <div className="grid-2">
              <div className="field">
                <label>Titre</label>
                <input required value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="field">
                <label>Objet</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div className="field">
                <label>Date / heure de début</label>
                <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              </div>
              <div className="field">
                <label>Date / heure de fin</label>
                <input
                  type="datetime-local"
                  value={endsAt}
                  min={startsAt || undefined}
                  onChange={(e) => setEndsAt(e.target.value)}
                />
              </div>
            </div>
            <div className="field">
              <label>Instructions</label>
              <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
            </div>
            <button className="btn btn-primary" type="submit">
              Ajouter
            </button>
          </form>
        </section>
      ) : null}

      <section className="admin-panel">
        {loading ? <ListSkeleton rows={3} /> : null}
        {!loading && events.length === 0 ? (
          <AdminEmpty icon="calendar" title="Aucun événement" hint="Ajoutez un premier rendez-vous ci-dessus." />
        ) : (
          <div className="admin-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Objet</th>
                  <th>Début → Fin</th>
                  <th>Instructions</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id}>
                    <td>
                      <strong>{ev.title}</strong>
                    </td>
                    <td>{ev.subject || '—'}</td>
                    <td>{formatAgendaAdminRange(ev.starts_at, ev.ends_at)}</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>{ev.instructions || '—'}</td>
                    <td>
                      {isAdmin ? (
                        <div className="admin-row-actions">
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void remove(ev.id)}>
                            Supprimer
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
